import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";
import _extends from "@babel/runtime/helpers/esm/extends";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import * as React from 'react';
import { useRef, useEffect, useState } from 'react';
import classNames from 'classnames';
import warning from "rc-util/es/warning";
import useMergedState from "rc-util/es/hooks/useMergedState";
import PickerTrigger from './PickerTrigger';
import PickerPanel from './PickerPanel';
import usePickerInput from './hooks/usePickerInput';
import getDataOrAriaProps, { toArray, getValue, updateValues } from './utils/miscUtil';
import { getDefaultFormat, getInputSize, elementsContains, getRangerPickerWidth } from './utils/uiUtil';
import PanelContext from './PanelContext';
import { isEqual, getClosingViewDate, isSameDate, isSameWeek, isSameQuarter, formatValue, parseValue, getDateTimeTextsFromFormat, isTimeDisabled } from './utils/dateUtil';
import useValueTexts from './hooks/useValueTexts';
import useTextValueMapping from './hooks/useTextValueMapping';
import RangeContext from './RangeContext';
import useRangeDisabled from './hooks/useRangeDisabled';
import getExtraFooter from './utils/getExtraFooter';
import getRanges from './utils/getRanges'; // import useRangeViewDates from './hooks/useRangeViewDates';

import { getStartEndDistance } from './hooks/useRangeViewDates';
import { createTimeZoneConfig } from './utils/timeZoneUtil'; // import useHoverValue from './hooks/useHoverValue';

function reorderValues(values, generateConfig) {
  if (values && values[0] && values[1] && generateConfig.isAfter(values[0], values[1])) {
    return [values[1], values[0]];
  }

  return values;
}

function canValueTrigger(value, index, disabled, allowEmpty) {
  if (value) {
    return true;
  }

  if (allowEmpty && allowEmpty[index]) {
    return true;
  }

  if (disabled[(index + 1) % 2]) {
    return true;
  }

  return false;
}

function InnerRangePicker(props) {
  var _classNames2;

  var _props$prefixCls = props.prefixCls,
      prefixCls = _props$prefixCls === void 0 ? 'rc-picker' : _props$prefixCls,
      id = props.id,
      style = props.style,
      className = props.className,
      popupStyle = props.popupStyle,
      dropdownClassName = props.dropdownClassName,
      transitionName = props.transitionName,
      dropdownAlign = props.dropdownAlign,
      getPopupContainer = props.getPopupContainer,
      baseGenerateConfig = props.generateConfig,
      _props$timeZone = props.timeZone,
      timeZone = _props$timeZone === void 0 ? 'Asia/Shanghai' : _props$timeZone,
      tzOffset = props.tzOffset,
      locale = props.locale,
      placeholder = props.placeholder,
      autoFocus = props.autoFocus,
      disabled = props.disabled,
      format = props.format,
      _props$picker = props.picker,
      picker = _props$picker === void 0 ? 'date' : _props$picker,
      showTime = props.showTime,
      use12Hours = props.use12Hours,
      _props$separator = props.separator,
      separator = _props$separator === void 0 ? '~' : _props$separator,
      value = props.value,
      defaultValue = props.defaultValue,
      defaultPickerValue = props.defaultPickerValue,
      open = props.open,
      defaultOpen = props.defaultOpen,
      disabledDate = props.disabledDate,
      _disabledTime = props.disabledTime,
      dateRender = props.dateRender,
      panelRender = props.panelRender,
      ranges = props.ranges,
      allowEmpty = props.allowEmpty,
      allowClear = props.allowClear,
      suffixIcon = props.suffixIcon,
      clearIcon = props.clearIcon,
      pickerRef = props.pickerRef,
      inputReadOnly = props.inputReadOnly,
      mode = props.mode,
      renderExtraFooter = props.renderExtraFooter,
      onChange = props.onChange,
      onOpenChange = props.onOpenChange,
      onPanelChange = props.onPanelChange,
      onCalendarChange = props.onCalendarChange,
      _onFocus = props.onFocus,
      onBlur = props.onBlur,
      onMouseEnter = props.onMouseEnter,
      onMouseLeave = props.onMouseLeave,
      _onOk = props.onOk,
      _onCancel = props.onCancel,
      _onKeyDown = props.onKeyDown,
      components = props.components,
      order = props.order,
      direction = props.direction,
      activePickerIndex = props.activePickerIndex,
      _props$autoComplete = props.autoComplete,
      autoComplete = _props$autoComplete === void 0 ? 'off' : _props$autoComplete,
      _props$confirmButton = props.confirmButton,
      confirmButton = _props$confirmButton === void 0 ? true : _props$confirmButton,
      okDisabled = props.okDisabled;
  var generateConfig = React.useMemo(function () {
    return createTimeZoneConfig(baseGenerateConfig, timeZone, tzOffset);
  }, [baseGenerateConfig, timeZone, tzOffset]);
  var panelPrefixCls = "".concat(prefixCls, "-datetime-panel"); // const needConfirmButton: boolean = (picker === 'date' && !!showTime) || picker === 'time';

  var needConfirmButton = confirmButton || picker === 'date' && !!showTime || picker === 'time'; // We record opened status here in case repeat open with picker

  var openRecordsRef = useRef({});
  var containerRef = useRef(null);
  var panelDivRef = useRef(null);
  var startInputDivRef = useRef(null);
  var endInputDivRef = useRef(null);
  var separatorRef = useRef(null);
  var startInputRef = useRef(null);
  var endInputRef = useRef(null);
  var spanRef = useRef(null); // ============================= Misc ==============================

  var formatList = toArray(getDefaultFormat(format, picker, showTime, use12Hours)); // Active picker

  var _useMergedState = useMergedState(0, {
    value: activePickerIndex
  }),
      _useMergedState2 = _slicedToArray(_useMergedState, 2),
      mergedActivePickerIndex = _useMergedState2[0],
      setMergedActivePickerIndex = _useMergedState2[1]; // Operation ref


  var operationRef = useRef(null);
  var mergedDisabled = React.useMemo(function () {
    if (Array.isArray(disabled)) {
      return disabled;
    }

    return [disabled || false, disabled || false];
  }, [disabled]); // 点击第一下

  var firstSelectedRef = useRef(true); // 上次选中values

  var preSelectedValues = useRef(null); // 缓存时分秒

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      cacheTimeValues = _useState2[0],
      setCacheTimeValues = _useState2[1]; // ============================= Value =============================


  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      mergedValue = _useState4[0],
      setInnerValue = _useState4[1];

  React.useEffect(function () {
    if (value || defaultValue) {
      preSelectedValues.current = value || defaultValue;
      setCacheTimeValues(value || defaultValue);
      setInnerValue(value || defaultValue);
    }
  }, [value]); // ============================= Value =============================
  // const [mergedValue, setInnerValue] = useMergedState<RangeValue<DateType>>(null, {
  //   value,
  //   defaultValue,
  //   postState: (values) =>
  //     picker === 'time' && !order ? values : reorderValues(values, generateConfig),
  // });
  // =========================== View Date ===========================
  // Config view panel
  // const [getViewDate, setViewDate] = useRangeViewDates({
  //   values: mergedValue,
  //   picker,
  //   defaultDates: defaultPickerValue,
  //   generateConfig,
  // });

  var viewDate = useRef(mergedValue || defaultPickerValue || [generateConfig.getNow(), getClosingViewDate(generateConfig.getNow(), picker, generateConfig, 1)]); // ========================= Select Values =========================

  var _useMergedState3 = useMergedState(mergedValue, {
    postState: function postState(values) {
      var postValues = values;

      if (mergedDisabled[0] && mergedDisabled[1]) {
        return postValues;
      } // Fill disabled unit


      // Fill disabled unit
      for (var i = 0; i < 2; i += 1) {
        if (mergedDisabled[i] && !getValue(postValues, i) && !getValue(allowEmpty, i)) {
          postValues = updateValues(postValues, generateConfig.getNow(), i);
        }
      }

      return postValues;
    }
  }),
      _useMergedState4 = _slicedToArray(_useMergedState3, 2),
      selectedValue = _useMergedState4[0],
      setSelectedValue = _useMergedState4[1]; // ============================= Modes =============================


  var _useMergedState5 = useMergedState([picker, picker], {
    value: mode
  }),
      _useMergedState6 = _slicedToArray(_useMergedState5, 2),
      mergedModes = _useMergedState6[0],
      setInnerModes = _useMergedState6[1]; // input-width


  var _useState5 = useState(0),
      _useState6 = _slicedToArray(_useState5, 2),
      inputWidth = _useState6[0],
      setInputWidth = _useState6[1];

  useEffect(function () {
    setInnerModes([picker, picker]); // 宽度不能小于61，

    var width = spanRef.current.clientWidth + 1;
    setInputWidth(width < 61 ? 61 : width);
  }, [picker]);

  var triggerModesChange = function triggerModesChange(modes, values) {
    setInnerModes(modes);

    if (onPanelChange) {
      onPanelChange(values, modes);
    }
  }; // ========================= Disable Date ==========================


  var _useRangeDisabled = useRangeDisabled({
    picker: picker,
    selectedValue: selectedValue,
    locale: locale,
    disabled: mergedDisabled,
    disabledDate: disabledDate,
    generateConfig: generateConfig
  }, openRecordsRef.current[1], openRecordsRef.current[0]),
      _useRangeDisabled2 = _slicedToArray(_useRangeDisabled, 2),
      disabledStartDate = _useRangeDisabled2[0],
      disabledEndDate = _useRangeDisabled2[1]; // ============================= Open ==============================


  var _useMergedState7 = useMergedState(false, {
    value: open,
    defaultValue: defaultOpen,
    postState: function postState(postOpen) {
      return mergedDisabled[mergedActivePickerIndex] ? false : postOpen;
    },
    onChange: function onChange(newOpen) {
      if (onOpenChange) {
        onOpenChange(newOpen);
      }

      if (!newOpen && operationRef.current && operationRef.current.onClose) {
        operationRef.current.onClose();
      }
    }
  }),
      _useMergedState8 = _slicedToArray(_useMergedState7, 2),
      mergedOpen = _useMergedState8[0],
      triggerInnerOpen = _useMergedState8[1];

  var startOpen = mergedOpen && mergedActivePickerIndex === 0;
  var endOpen = mergedOpen && mergedActivePickerIndex === 1; // ============================= Popup =============================
  // Popup min width

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      popupMinWidth = _useState8[0],
      setPopupMinWidth = _useState8[1];

  useEffect(function () {
    if (!mergedOpen && containerRef.current) {
      setPopupMinWidth(containerRef.current.offsetWidth);
    }
  }, [mergedOpen]); // ============================ Trigger ============================

  var triggerRef = React.useRef();

  function _triggerOpen(newOpen, index) {
    if (newOpen) {
      clearTimeout(triggerRef.current);
      openRecordsRef.current[index] = true;
      setMergedActivePickerIndex(index);
      triggerInnerOpen(newOpen); // Open to reset view date
      // if (!mergedOpen) {
      //   setViewDate(null, index);
      // }

      if (!mergedOpen && selectedValue) {
        viewDate.current = selectedValue;
      }
    } else if (confirmButton || !firstSelectedRef.current) {
      triggerInnerOpen(newOpen);
      firstSelectedRef.current = true;

      if (confirmButton) {
        setInnerValue(preSelectedValues.current);
        setSelectedValue(preSelectedValues.current);
        setCacheTimeValues(preSelectedValues.current);
      } // Clean up async
      // This makes ref not quick refresh in case user open another input with blur trigger


      var openRecords = openRecordsRef.current;
      triggerRef.current = setTimeout(function () {
        if (openRecords === openRecordsRef.current) {
          openRecordsRef.current = {};
        }

        if (confirmButton && preSelectedValues.current) {
          viewDate.current = preSelectedValues.current;
        }
      });
    }
  }

  function triggerOpenAndFocus(index) {
    _triggerOpen(true, index); // Use setTimeout to make sure panel DOM exists


    setTimeout(function () {
      var inputRef = [startInputRef, endInputRef][0];

      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  }

  function triggerChange(newValue, sourceIndex) {
    var values = newValue;
    var startValue = getValue(values, 0);
    var endValue = getValue(values, 1); // >>>>> Format start & end values

    if (startValue && endValue && generateConfig.isAfter(startValue, endValue)) {
      if ( // WeekPicker only compare week
      picker === 'week' && !isSameWeek(generateConfig, locale.locale, startValue, endValue) || // QuotaPicker only compare week
      picker === 'quarter' && !isSameQuarter(generateConfig, startValue, endValue) || // Other non-TimePicker compare date
      picker !== 'week' && picker !== 'quarter' && picker !== 'time' && !isSameDate(generateConfig, startValue, endValue)) {
        // Clean up end date when start date is after end date
        if (sourceIndex === 0) {
          values = [startValue, null];
          endValue = null;
        } else {
          startValue = null;
          values = [null, endValue];
        } // Clean up cache since invalidate


        openRecordsRef.current = _defineProperty({}, sourceIndex, true);
      } else if (picker !== 'time' || order !== false) {
        // Reorder when in same date
        values = reorderValues(values, generateConfig);
      }
    }

    setSelectedValue(values);
    var startStr = values && values[0] ? formatValue(values[0], {
      generateConfig: generateConfig,
      locale: locale,
      format: formatList[0]
    }) : '';
    var endStr = values && values[1] ? formatValue(values[1], {
      generateConfig: generateConfig,
      locale: locale,
      format: formatList[0]
    }) : '';

    if (onCalendarChange) {
      // const info: RangeInfo = { range: sourceIndex === 0 ? 'start' : 'end' };
      var info = {
        range: firstSelectedRef.current ? 'start' : 'end'
      };
      onCalendarChange(values, [startStr, endStr], info);
    } // >>>>> Trigger `onChange` event


    var canStartValueTrigger = canValueTrigger(startValue, 0, mergedDisabled, allowEmpty);
    var canEndValueTrigger = canValueTrigger(endValue, 1, mergedDisabled, allowEmpty);
    var canTrigger = values === null || canStartValueTrigger && canEndValueTrigger;

    if (canTrigger) {
      // Trigger onChange only when value is validate
      setInnerValue(values);

      if (onChange && (!isEqual(generateConfig, getValue(mergedValue, 0), startValue) || !isEqual(generateConfig, getValue(mergedValue, 1), endValue)) && !needConfirmButton) {
        onChange(values, [startStr, endStr]);
      }
    } // >>>>> Open picker when
    // Always open another picker if possible
    // let nextOpenIndex: 0 | 1 = null;
    // if (sourceIndex === 0 && !mergedDisabled[1]) {
    //   nextOpenIndex = 1;
    // } else if (sourceIndex === 1 && !mergedDisabled[0]) {
    //   nextOpenIndex = 0;
    // }
    // if (
    //   nextOpenIndex !== null &&
    //   nextOpenIndex !== mergedActivePickerIndex &&
    //   (!openRecordsRef.current[nextOpenIndex] || !getValue(values, nextOpenIndex)) &&
    //   getValue(values, sourceIndex)
    // ) {
    //   // Delay to focus to avoid input blur trigger expired selectedValues
    //   triggerOpenAndFocus(nextOpenIndex);
    // } else {
    //   triggerOpen(false, sourceIndex);
    // }


    if (!needConfirmButton && !firstSelectedRef.current) {
      _triggerOpen(false, sourceIndex);
    }
  }

  var forwardKeyDown = function forwardKeyDown(e) {
    if (mergedOpen && operationRef.current && operationRef.current.onKeyDown) {
      // Let popup panel handle keyboard
      return operationRef.current.onKeyDown(e);
    }
    /* istanbul ignore next */

    /* eslint-disable no-lone-blocks */


    {
      warning(false, 'Picker not correct forward KeyDown operation. Please help to fire issue about this.');
      return false;
    }
  }; // ============================= Text ==============================


  var sharedTextHooksProps = {
    formatList: formatList,
    generateConfig: generateConfig,
    locale: locale
  };

  var _useValueTexts = useValueTexts( // getValue(selectedValue, 0),
  getValue(mergedValue, 0), sharedTextHooksProps),
      _useValueTexts2 = _slicedToArray(_useValueTexts, 2),
      startValueTexts = _useValueTexts2[0],
      firstStartValueText = _useValueTexts2[1];

  var _useValueTexts3 = useValueTexts( // getValue(selectedValue, 1),
  getValue(mergedValue, 1), sharedTextHooksProps),
      _useValueTexts4 = _slicedToArray(_useValueTexts3, 2),
      endValueTexts = _useValueTexts4[0],
      firstEndValueText = _useValueTexts4[1];

  var _onTextChange = function onTextChange(newText, index) {
    var inputDate = parseValue(newText, {
      locale: locale,
      formatList: formatList,
      generateConfig: generateConfig
    });
    var disabledFunc = index === 0 ? disabledStartDate : disabledEndDate;

    if (inputDate && !disabledFunc(inputDate)) {
      setSelectedValue(updateValues(selectedValue, inputDate, index)); // setViewDate(inputDate, index);

      viewDate.current = updateValues(viewDate.current, inputDate, index);
    }
  };

  var _useTextValueMapping = useTextValueMapping({
    valueTexts: startValueTexts,
    onTextChange: function onTextChange(newText) {
      return _onTextChange(newText, 0);
    }
  }),
      _useTextValueMapping2 = _slicedToArray(_useTextValueMapping, 3),
      startText = _useTextValueMapping2[0],
      triggerStartTextChange = _useTextValueMapping2[1],
      resetStartText = _useTextValueMapping2[2];

  var _useTextValueMapping3 = useTextValueMapping({
    valueTexts: endValueTexts,
    onTextChange: function onTextChange(newText) {
      return _onTextChange(newText, 1);
    }
  }),
      _useTextValueMapping4 = _slicedToArray(_useTextValueMapping3, 3),
      endText = _useTextValueMapping4[0],
      triggerEndTextChange = _useTextValueMapping4[1],
      resetEndText = _useTextValueMapping4[2];

  var _useState9 = useState(null),
      _useState10 = _slicedToArray(_useState9, 2),
      rangeHoverValue = _useState10[0],
      setRangeHoverValue = _useState10[1]; // ========================== Hover Range ==========================


  var _useState11 = useState(null),
      _useState12 = _slicedToArray(_useState11, 2),
      hoverRangedValue = _useState12[0],
      setHoverRangedValue = _useState12[1]; // const [startHoverValue, onStartEnter, onStartLeave] = useHoverValue(startText, {
  //   formatList,
  //   generateConfig,
  //   locale,
  // });
  // const [endHoverValue, onEndEnter, onEndLeave] = useHoverValue(endText, {
  //   formatList,
  //   generateConfig,
  //   locale,
  // });


  var onDateMouseEnter = function onDateMouseEnter(date) {
    // setHoverRangedValue(updateValues(selectedValue, date, mergedActivePickerIndex));
    setHoverRangedValue(refreshValues(selectedValue, date, firstSelectedRef.current));

    if (mergedActivePickerIndex === 0) {// onStartEnter(date);
      // onStartEnter(null);
    } else {// onEndEnter(date);
      // onEndEnter(null);
    }
  };

  var onDateMouseLeave = function onDateMouseLeave() {
    // setHoverRangedValue(updateValues(selectedValue, null, mergedActivePickerIndex));
    setHoverRangedValue(refreshValues(selectedValue, null, firstSelectedRef.current));

    if (mergedActivePickerIndex === 0) {// onStartLeave();
    } else {// onEndLeave();
    }
  }; // ============================= Input =============================


  var getSharedInputHookProps = function getSharedInputHookProps(index, resetText) {
    return {
      blurToCancel: needConfirmButton,
      forwardKeyDown: forwardKeyDown,
      onBlur: onBlur,
      isClickOutside: function isClickOutside(target) {
        return !elementsContains([panelDivRef.current, startInputDivRef.current, endInputDivRef.current, containerRef.current], target);
      },
      onFocus: function onFocus(e) {
        setMergedActivePickerIndex(index);

        if (_onFocus) {
          _onFocus(e);
        }
      },
      triggerOpen: function triggerOpen(newOpen) {
        _triggerOpen(newOpen, index);
      },
      onSubmit: function onSubmit() {
        if ( // When user typing disabledDate with keyboard and enter, this value will be empty
        !selectedValue || // Normal disabled check
        disabledDate && disabledDate(selectedValue[index])) {
          return false;
        }

        triggerChange(selectedValue, index);
        resetText();
      },
      onCancel: function onCancel() {
        _triggerOpen(false, index);

        setSelectedValue(mergedValue);
        resetText();
      }
    };
  };

  var _usePickerInput = usePickerInput(_objectSpread(_objectSpread({}, getSharedInputHookProps(0, resetStartText)), {}, {
    open: startOpen,
    value: startText,
    onKeyDown: function onKeyDown(e, preventDefault) {
      _onKeyDown === null || _onKeyDown === void 0 ? void 0 : _onKeyDown(e, preventDefault);
    }
  })),
      _usePickerInput2 = _slicedToArray(_usePickerInput, 2),
      startInputProps = _usePickerInput2[0],
      _usePickerInput2$ = _usePickerInput2[1],
      startFocused = _usePickerInput2$.focused,
      startTyping = _usePickerInput2$.typing;

  var _usePickerInput3 = usePickerInput(_objectSpread(_objectSpread({}, getSharedInputHookProps(1, resetEndText)), {}, {
    open: endOpen,
    value: endText,
    onKeyDown: function onKeyDown(e, preventDefault) {
      _onKeyDown === null || _onKeyDown === void 0 ? void 0 : _onKeyDown(e, preventDefault);
    }
  })),
      _usePickerInput4 = _slicedToArray(_usePickerInput3, 2),
      endInputProps = _usePickerInput4[0],
      _usePickerInput4$ = _usePickerInput4[1],
      endFocused = _usePickerInput4$.focused,
      endTyping = _usePickerInput4$.typing; // ========================== Click Picker ==========================


  var onPickerClick = function onPickerClick(e) {
    // When click inside the picker & outside the picker's input elements
    // the panel should still be opened
    if (!mergedOpen && !startInputRef.current.contains(e.target) && !endInputRef.current.contains(e.target)) {
      if (!mergedDisabled[0]) {
        triggerOpenAndFocus(0);
      } else if (!mergedDisabled[1]) {
        triggerOpenAndFocus(1);
      }
    }
  };

  var onPickerMouseDown = function onPickerMouseDown(e) {
    // shouldn't affect input elements if picker is active
    if (mergedOpen && (startFocused || endFocused) && !startInputRef.current.contains(e.target) && !endInputRef.current.contains(e.target)) {
      e.preventDefault();
    }
  }; // ============================= Sync ==============================
  // Close should sync back with text value


  var startStr = mergedValue && mergedValue[0] ? formatValue(mergedValue[0], {
    locale: locale,
    format: 'YYYYMMDDHHmmss',
    generateConfig: generateConfig
  }) : '';
  var endStr = mergedValue && mergedValue[1] ? formatValue(mergedValue[1], {
    locale: locale,
    format: 'YYYYMMDDHHmmss',
    generateConfig: generateConfig
  }) : '';
  useEffect(function () {
    if (!mergedOpen) {
      setSelectedValue(mergedValue);

      if (!startValueTexts.length || startValueTexts[0] === '') {
        triggerStartTextChange('');
      } else if (firstStartValueText !== startText) {
        resetStartText();
      }

      if (!endValueTexts.length || endValueTexts[0] === '') {
        triggerEndTextChange('');
      } else if (firstEndValueText !== endText) {
        resetEndText();
      }
    }
  }, [mergedOpen, startValueTexts, endValueTexts]); // Sync innerValue with control mode

  useEffect(function () {
    setSelectedValue(mergedValue);
  }, [startStr, endStr]); // ============================ Warning ============================

  if (process.env.NODE_ENV !== 'production') {
    if (value && Array.isArray(disabled) && (getValue(disabled, 0) && !getValue(value, 0) || getValue(disabled, 1) && !getValue(value, 1))) {
      warning(false, '`disabled` should not set with empty `value`. You should set `allowEmpty` or `value` instead.');
    }
  } // ============================ Private ============================


  if (pickerRef) {
    pickerRef.current = {
      focus: function focus() {
        if (startInputRef.current) {
          startInputRef.current.focus();
        }
      },
      blur: function blur() {
        if (startInputRef.current) {
          startInputRef.current.blur();
        }

        if (endInputRef.current) {
          endInputRef.current.blur();
        }
      }
    };
  } // ============================ Ranges =============================


  var rangeLabels = Object.keys(ranges || {});
  var rangeList = rangeLabels.map(function (label) {
    var range = ranges[label];
    var newValues = typeof range === 'function' ? range() : range;
    return {
      label: label,
      onClick: function onClick() {
        triggerChange(newValues, null);

        _triggerOpen(false, mergedActivePickerIndex);
      },
      onMouseEnter: function onMouseEnter() {
        setRangeHoverValue(newValues);
      },
      onMouseLeave: function onMouseLeave() {
        setRangeHoverValue(null);
      }
    };
  });

  function changeDateTime(dateValue, timeValue) {
    var newValue = dateValue;

    if (timeValue && dateValue) {
      newValue = generateConfig.setHour(newValue, generateConfig.getHour(timeValue));
      newValue = generateConfig.setMinute(newValue, generateConfig.getMinute(timeValue));
      newValue = generateConfig.setSecond(newValue, generateConfig.getSecond(timeValue));
    }

    return newValue;
  }

  function handleTimeSelect(timeValue, panelPosition) {
    var index = panelPosition === 'left' ? 0 : 1;
    var newValue = getValue(mergedValue, index);

    if (timeValue && newValue) {
      // 融合时分秒
      newValue = changeDateTime(newValue, timeValue);
      var newMergedValue = updateValues(mergedValue, newValue, index);
      var newCacheTimeValues = updateValues(cacheTimeValues, newValue, index);
      setInnerValue(newMergedValue);
      setCacheTimeValues(newCacheTimeValues);

      if (onCalendarChange) {
        onCalendarChange(newMergedValue, [formatValue(newMergedValue[0], {
          generateConfig: generateConfig,
          locale: locale,
          format: formatList[0]
        }), formatValue(newMergedValue[1], {
          generateConfig: generateConfig,
          locale: locale,
          format: formatList[0]
        })], {
          range: panelPosition === 'left' ? 'start' : 'end'
        });
      }
    }
  } // ============================= input输入框联动 =============================


  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isInvalidStartText = _React$useState2[0],
      setIsInvalidStartText = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      isInvalidEndText = _React$useState4[0],
      setIsInvalidEndText = _React$useState4[1];

  React.useEffect(function () {
    if (mergedOpen && picker === 'date' && showTime) {
      setTimeout(function () {
        var _panelDivRef$current;

        var startPanel = (_panelDivRef$current = panelDivRef.current) === null || _panelDivRef$current === void 0 ? void 0 : _panelDivRef$current.querySelectorAll(".".concat(prefixCls, "-datetime-panel"))[0];
        var panelDateInput = startPanel === null || startPanel === void 0 ? void 0 : startPanel.querySelector(".".concat(panelPrefixCls, "-wrap-datetime-date input"));
        var panelTimeInput = startPanel === null || startPanel === void 0 ? void 0 : startPanel.querySelector(".".concat(panelPrefixCls, "-wrap-datetime-time input"));

        if (panelDateInput && panelTimeInput) {
          var _getDateTimeTextsFrom = getDateTimeTextsFromFormat(startText, formatList[0], {
            generateConfig: generateConfig,
            locale: locale,
            formatList: formatList
          }),
              dateText = _getDateTimeTextsFrom.dateText,
              timeText = _getDateTimeTextsFrom.timeText;

          panelDateInput.value = dateText;
          panelTimeInput.value = timeText;
          var inputDate = parseValue(startText, {
            locale: locale,
            formatList: formatList,
            generateConfig: generateConfig
          });
          setIsInvalidStartText(!inputDate);
        }
      }, 0);
    }
  }, [mergedOpen, picker, showTime, startText]);
  React.useEffect(function () {
    if (mergedOpen && picker === 'date' && showTime) {
      setTimeout(function () {
        var _panelDivRef$current2;

        var endPanel = (_panelDivRef$current2 = panelDivRef.current) === null || _panelDivRef$current2 === void 0 ? void 0 : _panelDivRef$current2.querySelectorAll(".".concat(prefixCls, "-datetime-panel"))[1];
        var panelDateInput = endPanel === null || endPanel === void 0 ? void 0 : endPanel.querySelector(".".concat(panelPrefixCls, "-wrap-datetime-date input"));
        var panelTimeInput = endPanel === null || endPanel === void 0 ? void 0 : endPanel.querySelector(".".concat(panelPrefixCls, "-wrap-datetime-time input"));

        if (panelDateInput && panelTimeInput) {
          var _getDateTimeTextsFrom2 = getDateTimeTextsFromFormat(endText, formatList[0], {
            generateConfig: generateConfig,
            locale: locale,
            formatList: formatList
          }),
              dateText = _getDateTimeTextsFrom2.dateText,
              timeText = _getDateTimeTextsFrom2.timeText;

          panelDateInput.value = dateText;
          panelTimeInput.value = timeText;
          var inputDate = parseValue(endText, {
            locale: locale,
            formatList: formatList,
            generateConfig: generateConfig
          });
          setIsInvalidEndText(!inputDate);
        }
      }, 0);
    }
  }, [mergedOpen, picker, showTime, endText]); // ============================= Panel =============================

  function renderPanel() {
    var panelPosition = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    var panelProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var activeIndex = panelPosition === 'left' ? 0 : 1;
    var panelHoverRangedValue = null;

    if (mergedOpen && hoverRangedValue && hoverRangedValue[0] && hoverRangedValue[1] && generateConfig.isAfter(hoverRangedValue[1], hoverRangedValue[0])) {
      panelHoverRangedValue = hoverRangedValue;
    }

    var panelShowTime = showTime; // if (showTime && typeof showTime === 'object' && showTime.defaultValue) {

    if (showTime && _typeof(showTime) === 'object') {
      var timeDefaultValues = cacheTimeValues || showTime.defaultValue;
      panelShowTime = _objectSpread(_objectSpread({}, showTime), {}, {
        // defaultValue: getValue(timeDefaultValues, mergedActivePickerIndex) || undefined,
        defaultValue: getValue(timeDefaultValues, activeIndex) || undefined
      });
    }

    var panelDateRender = null;

    if (dateRender) {
      panelDateRender = function panelDateRender(date, today) {
        return dateRender(date, today, {
          range: mergedActivePickerIndex ? 'end' : 'start'
        });
      };
    }

    var panelDateInputChange = function panelDateInputChange() {
      var _panelDivRef$current3;

      var panel = (_panelDivRef$current3 = panelDivRef.current) === null || _panelDivRef$current3 === void 0 ? void 0 : _panelDivRef$current3.querySelectorAll(".".concat(prefixCls, "-datetime-panel"))[activeIndex];
      var panelDateInput = panel === null || panel === void 0 ? void 0 : panel.querySelector(".".concat(panelPrefixCls, "-wrap-datetime-date input"));
      var panelTimeInput = panel === null || panel === void 0 ? void 0 : panel.querySelector(".".concat(panelPrefixCls, "-wrap-datetime-time input"));
      var mergeText = "".concat(panelDateInput.value, " ").concat(panelTimeInput.value);
      var inputDate = parseValue(mergeText, {
        locale: locale,
        formatList: formatList,
        generateConfig: generateConfig
      });
      var disabledFunc = activeIndex === 0 ? disabledStartDate : disabledEndDate;
      var checkTimeDisabled = _disabledTime ? function (date) {
        return _disabledTime(date, panelPosition);
      } : undefined;

      if (activeIndex === 0) {
        triggerStartTextChange(mergeText);

        if (inputDate && !disabledFunc(inputDate) && !isTimeDisabled(generateConfig, inputDate, checkTimeDisabled)) {
          var timeValues = updateValues(mergedValue, inputDate, 0);
          onCalendarChange === null || onCalendarChange === void 0 ? void 0 : onCalendarChange(timeValues, [mergeText, endText], {
            range: 'start'
          });
          setInnerValue(timeValues);
          setCacheTimeValues(timeValues);
        }
      } else if (activeIndex === 1) {
        triggerEndTextChange(mergeText);

        if (inputDate && !disabledFunc(inputDate) && !isTimeDisabled(generateConfig, inputDate, checkTimeDisabled)) {
          var _timeValues = updateValues(mergedValue, inputDate, 1);

          onCalendarChange === null || onCalendarChange === void 0 ? void 0 : onCalendarChange(_timeValues, [startText, mergeText], {
            range: 'end'
          });
          setInnerValue(_timeValues);
          setCacheTimeValues(_timeValues);
        }
      }
    };

    return /*#__PURE__*/React.createElement(RangeContext.Provider, {
      value: {
        inRange: true,
        panelPosition: panelPosition,
        rangedValue: rangeHoverValue || selectedValue,
        hoverRangedValue: panelHoverRangedValue,
        isMonthFar: getStartEndDistance(getValue(viewDate.current, 0) || generateConfig.getNow(), getValue(viewDate.current, 1) || generateConfig.getNow(), picker, generateConfig) === 'far',
        isYearFar: getStartEndDistance(getValue(viewDate.current, 0) || generateConfig.getNow(), getValue(viewDate.current, 1) || generateConfig.getNow(), 'month', generateConfig) === 'far',
        timeValue: getValue(mergedValue, panelPosition === 'left' ? 0 : 1),
        onTimeSelect: function onTimeSelect(time) {
          return handleTimeSelect(time, panelPosition);
        }
      }
    }, /*#__PURE__*/React.createElement(PickerPanel, _extends({}, props, panelProps, {
      dateRender: panelDateRender,
      showTime: panelShowTime,
      mode: mergedModes[mergedActivePickerIndex],
      generateConfig: generateConfig,
      style: undefined,
      direction: direction // disabledDate={mergedActivePickerIndex === 0 ? disabledStartDate : disabledEndDate}
      ,
      disabledTime: function disabledTime(date) {
        if (_disabledTime) {
          // return disabledTime(date, mergedActivePickerIndex === 0 ? 'start' : 'end');
          var position = 'left';

          if (panelPosition === 'right') {
            position = 'right';
          }

          return _disabledTime(date, position);
        }

        return false;
      },
      className: classNames(_defineProperty({}, "".concat(prefixCls, "-panel-focused"), mergedActivePickerIndex === 0 ? !startTyping : !endTyping)) // value={getValue(selectedValue, mergedActivePickerIndex)}
      ,
      value: getValue(selectedValue, panelPosition === 'left' ? 0 : 1),
      locale: locale,
      panelPrefixCls: panelPrefixCls,
      panelDateInputChange: panelDateInputChange,
      format: formatList[0],
      tabIndex: -1,
      onPanelChange: function onPanelChange(date, newMode) {
        // clear hover value when panel change
        // if (mergedActivePickerIndex === 0) {
        //   onStartLeave(true);
        // }
        // if (mergedActivePickerIndex === 1) {
        //   onEndLeave(true);
        // }
        triggerModesChange(updateValues(mergedModes, newMode, mergedActivePickerIndex), updateValues(selectedValue, date, mergedActivePickerIndex)); // let viewDate = date;
        // if (panelPosition === 'right' && mergedModes[mergedActivePickerIndex] === newMode) {
        //   viewDate = getClosingViewDate(viewDate, newMode as any, generateConfig, -1);
        // }
        // setViewDate(viewDate, mergedActivePickerIndex);
      },
      onOk: null,
      onSelect: undefined,
      onChange: undefined,
      defaultValue: mergedActivePickerIndex === 0 ? getValue(selectedValue, 1) : getValue(selectedValue, 0)
    })));
  }

  var arrowLeft = 0;
  var panelLeft = 0;

  if (mergedActivePickerIndex && startInputDivRef.current && separatorRef.current && panelDivRef.current) {
    // Arrow offset
    arrowLeft = startInputDivRef.current.offsetWidth + separatorRef.current.offsetWidth;

    if (panelDivRef.current.offsetWidth && arrowLeft > panelDivRef.current.offsetWidth) {
      panelLeft = arrowLeft;
    }
  } // const arrowPositionStyle = direction === 'rtl' ? { right: arrowLeft } : { left: arrowLeft };


  function renderPanels() {
    var panels;
    var extraNode = getExtraFooter(prefixCls, mergedModes[mergedActivePickerIndex], renderExtraFooter);
    var rangesNode = getRanges({
      prefixCls: prefixCls,
      components: components,
      needConfirmButton: needConfirmButton,
      okDisabled: // !getValue(selectedValue, mergedActivePickerIndex) ||
      okDisabled || isInvalidStartText || isInvalidEndText || !getValue(selectedValue, 0) || !getValue(selectedValue, 1) || disabledDate && disabledDate(selectedValue[mergedActivePickerIndex]),
      locale: locale,
      rangeList: rangeList,
      isRange: true,
      onOk: function onOk() {
        var time1 = getValue(selectedValue, 0);
        var time2 = getValue(selectedValue, 1); // if (getValue(selectedValue, mergedActivePickerIndex)) {

        if (time1 && time2) {
          // triggerChange(selectedValue, mergedActivePickerIndex);
          preSelectedValues.current = selectedValue;
          setSelectedValue(selectedValue);

          _triggerOpen(false, mergedActivePickerIndex);

          if (_onOk) {
            _onOk(selectedValue, [formatValue(selectedValue[0], {
              generateConfig: generateConfig,
              locale: locale,
              format: formatList[0]
            }), formatValue(selectedValue[1], {
              generateConfig: generateConfig,
              locale: locale,
              format: formatList[0]
            })]);
          }
        }
      },
      onCancel: function onCancel() {
        _triggerOpen(false, mergedActivePickerIndex);

        if (_onCancel) {
          _onCancel(preSelectedValues.current);
        }
      }
    }); // if (picker !== 'time' && !showTime) {
    // const viewDate = getViewDate(mergedActivePickerIndex);
    // const nextViewDate = getClosingViewDate(viewDate, picker, generateConfig);

    var currentMode = mergedModes[mergedActivePickerIndex];
    var showDoublePanel = currentMode === picker; // const leftPanel = renderPanel(showDoublePanel ? 'left' : false, {
    //   pickerValue: viewDate,
    //   onPickerValueChange: (newViewDate) => {
    //     setViewDate(newViewDate, mergedActivePickerIndex);
    //   },
    // });
    // const rightPanel = renderPanel('right', {
    //   pickerValue: nextViewDate,
    //   onPickerValueChange: (newViewDate) => {
    //     setViewDate(
    //       getClosingViewDate(newViewDate, picker, generateConfig, -1),
    //       mergedActivePickerIndex,
    //     );
    //   },
    // });

    var leftPickValue = getValue(viewDate.current, 0) || generateConfig.getNow();
    var rightPickValue = getValue(viewDate.current, 1) || generateConfig.getNow();

    if (getStartEndDistance(leftPickValue, rightPickValue, picker, generateConfig) === 'same') {
      rightPickValue = getClosingViewDate(leftPickValue, picker, generateConfig);
    }

    var leftPanel = renderPanel(showDoublePanel ? 'left' : false, {
      pickerValue: leftPickValue,
      onPickerValueChange: function onPickerValueChange(newViewDate) {
        viewDate.current = updateValues(viewDate.current, newViewDate, 0);
      }
    });
    var rightPanel = renderPanel('right', {
      pickerValue: rightPickValue,
      onPickerValueChange: function onPickerValueChange(newViewDate) {
        viewDate.current = updateValues(viewDate.current, newViewDate, 1);
      }
    });

    if (direction === 'rtl') {
      panels = /*#__PURE__*/React.createElement(React.Fragment, null, rightPanel, showDoublePanel && leftPanel);
    } else {
      panels = /*#__PURE__*/React.createElement(React.Fragment, null, leftPanel, showDoublePanel && rightPanel);
    } // } else {
    //   panels = renderPanel();
    // }


    var mergedNodes = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "".concat(prefixCls, "-panels")
    }, panels), (extraNode || rangesNode) && /*#__PURE__*/React.createElement("div", {
      className: "".concat(prefixCls, "-footer")
    }, extraNode, rangesNode));

    if (panelRender) {
      mergedNodes = panelRender(mergedNodes);
    }

    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(prefixCls, "-panel-container"),
      style: {
        marginLeft: panelLeft
      },
      ref: panelDivRef
    }, mergedNodes);
  }

  var rangePanel = /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(prefixCls, "-range-wrapper"), "".concat(prefixCls, "-").concat(picker, "-range-wrapper")),
    style: {
      minWidth: popupMinWidth
    }
  }, renderPanels()); // ============================= Icons =============================

  var suffixNode;

  if (suffixIcon) {
    suffixNode = /*#__PURE__*/React.createElement("span", {
      className: "".concat(prefixCls, "-suffix")
    }, suffixIcon);
  }

  var clearNode;

  if (allowClear && (getValue(mergedValue, 0) && !mergedDisabled[0] || getValue(mergedValue, 1) && !mergedDisabled[1]) && !mergedOpen) {
    clearNode = /*#__PURE__*/React.createElement("span", {
      onMouseDown: function onMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();
      },
      onMouseUp: function onMouseUp(e) {
        e.preventDefault();
        e.stopPropagation();
        var values = mergedValue;

        if (!mergedDisabled[0]) {
          values = updateValues(values, null, 0);
        }

        if (!mergedDisabled[1]) {
          values = updateValues(values, null, 1);
        }

        preSelectedValues.current = values;
        triggerChange(values, null);

        _triggerOpen(false, mergedActivePickerIndex);

        _onOk(null, ['', '']);
      },
      className: "".concat(prefixCls, "-clear")
    }, clearIcon || /*#__PURE__*/React.createElement("span", {
      className: "".concat(prefixCls, "-clear-btn")
    }));
  }

  var inputSharedProps = {
    size: getInputSize(picker, formatList[0], generateConfig)
  }; // let activeBarLeft: number = 0;
  // let activeBarWidth: number = 0;
  // if (startInputDivRef.current && endInputDivRef.current && separatorRef.current) {
  //   if (mergedActivePickerIndex === 0) {
  //     activeBarWidth = startInputDivRef.current.offsetWidth;
  //   } else {
  //     activeBarLeft = arrowLeft;
  //     activeBarWidth = endInputDivRef.current.offsetWidth;
  //   }
  // }
  // const activeBarPositionStyle =
  //   direction === 'rtl' ? { right: activeBarLeft } : { left: activeBarLeft };
  // ============================ Return =============================

  function refreshValues(values, nextValue, firstSelected) {
    // const timeValues: DateType[] | null = [null, null];
    var timeValues = [generateConfig.getNow(), generateConfig.getNow()]; // 缓存的时分秒

    if (cacheTimeValues && cacheTimeValues[0]) {
      timeValues[0] = cacheTimeValues[0];
    }

    if (cacheTimeValues && cacheTimeValues[1]) {
      timeValues[1] = cacheTimeValues[1];
    }

    var newValues = [getValue(values, 0), getValue(values, 1)];

    if (firstSelected) {
      newValues[0] = typeof nextValue === 'function' ? nextValue(newValues[0]) : nextValue;
      newValues[1] = null;
    } else {
      newValues[1] = typeof nextValue === 'function' ? nextValue(newValues[1]) : nextValue;
    }

    if (!newValues[0] && !newValues[1]) {
      return null;
    }

    if (newValues[0] && newValues[1] && generateConfig.isAfter(newValues[0], newValues[1])) {
      newValues = [newValues[1], newValues[0]];
    }

    if (timeValues && timeValues[0]) {
      newValues[0] = changeDateTime(newValues[0], timeValues[0]);
    }

    if (timeValues && timeValues[1]) {
      newValues[1] = changeDateTime(newValues[1], timeValues[1]);
    }

    return newValues;
  }

  var onContextSelect = function onContextSelect(date, type) {
    // const values = updateValues(selectedValue, date, mergedActivePickerIndex);
    var values = refreshValues(selectedValue, date, firstSelectedRef.current); // if (type === 'submit' || (type !== 'key' && !needConfirmButton)) {

    if (type === 'submit' || type === 'mouse') {
      // triggerChange will also update selected values
      triggerChange(values, mergedActivePickerIndex); // clear hover value style
      // if (mergedActivePickerIndex === 0) {
      //   onStartLeave();
      // } else {
      //   onEndLeave();
      // }
    } else {
      setSelectedValue(values);
    }

    firstSelectedRef.current = !firstSelectedRef.current;
  };

  return /*#__PURE__*/React.createElement(PanelContext.Provider, {
    value: {
      operationRef: operationRef,
      hideHeader: picker === 'time',
      onDateMouseEnter: onDateMouseEnter,
      onDateMouseLeave: onDateMouseLeave,
      hideRanges: true,
      onSelect: onContextSelect,
      open: mergedOpen
    }
  }, /*#__PURE__*/React.createElement(PickerTrigger, {
    visible: mergedOpen,
    popupElement: rangePanel,
    popupStyle: popupStyle,
    prefixCls: prefixCls,
    dropdownClassName: dropdownClassName,
    dropdownAlign: dropdownAlign,
    getPopupContainer: getPopupContainer,
    transitionName: transitionName,
    range: true,
    direction: direction
  }, /*#__PURE__*/React.createElement("div", _extends({
    ref: containerRef,
    className: classNames(prefixCls, "".concat(prefixCls, "-range"), className, (_classNames2 = {}, _defineProperty(_classNames2, "".concat(prefixCls, "-disabled"), mergedDisabled[0] && mergedDisabled[1]), _defineProperty(_classNames2, "".concat(prefixCls, "-focused"), mergedActivePickerIndex === 0 ? startFocused : endFocused), _defineProperty(_classNames2, "".concat(prefixCls, "-rtl"), direction === 'rtl'), _classNames2)),
    style: _objectSpread({
      width: getRangerPickerWidth(formatList[0], generateConfig)
    }, style),
    onClick: onPickerClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onMouseDown: onPickerMouseDown
  }, getDataOrAriaProps(props)), /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(prefixCls, "-input"), _defineProperty({}, "".concat(prefixCls, "-input-active"), mergedActivePickerIndex === 0)),
    ref: startInputDivRef,
    style: {
      width: inputWidth
    }
  }, /*#__PURE__*/React.createElement("input", _extends({
    id: id,
    disabled: mergedDisabled[0],
    readOnly: inputReadOnly || typeof formatList[0] === 'function' || !startTyping,
    value: startText,
    onChange: function onChange(e) {
      triggerStartTextChange(e.target.value);
    },
    autoFocus: autoFocus,
    placeholder: getValue(placeholder, 0) || '',
    ref: startInputRef
  }, startInputProps, inputSharedProps, {
    autoComplete: autoComplete
  }))), /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefixCls, "-range-separator"),
    ref: separatorRef
  }, separator), /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(prefixCls, "-input"), _defineProperty({}, "".concat(prefixCls, "-input-active"), mergedActivePickerIndex === 1)),
    ref: endInputDivRef,
    style: {
      width: inputWidth
    }
  }, /*#__PURE__*/React.createElement("input", _extends({
    disabled: mergedDisabled[1],
    readOnly: inputReadOnly || typeof formatList[0] === 'function' || !endTyping,
    value: endText,
    onChange: function onChange(e) {
      triggerEndTextChange(e.target.value);
    },
    placeholder: getValue(placeholder, 1) || '',
    ref: endInputRef
  }, endInputProps, inputSharedProps, {
    autoComplete: autoComplete
  }))), /*#__PURE__*/React.createElement("span", {
    ref: spanRef,
    style: {
      position: 'absolute',
      zIndex: '-9999',
      visibility: 'hidden'
    }
  }, formatValue(generateConfig.getNow(), {
    generateConfig: generateConfig,
    locale: locale,
    format: formatList[0]
  })), suffixNode, clearNode)));
} // Wrap with class component to enable pass generic with instance method


var RangePicker = /*#__PURE__*/function (_React$Component) {
  _inherits(RangePicker, _React$Component);

  var _super = _createSuper(RangePicker);

  function RangePicker() {
    var _this;

    _classCallCheck(this, RangePicker);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this.pickerRef = /*#__PURE__*/React.createRef();

    _this.focus = function () {
      if (_this.pickerRef.current) {
        _this.pickerRef.current.focus();
      }
    };

    _this.blur = function () {
      if (_this.pickerRef.current) {
        _this.pickerRef.current.blur();
      }
    };

    return _this;
  }

  _createClass(RangePicker, [{
    key: "render",
    value: function render() {
      return /*#__PURE__*/React.createElement(InnerRangePicker, _extends({}, this.props, {
        pickerRef: this.pickerRef
      }));
    }
  }]);

  return RangePicker;
}(React.Component);

export default RangePicker;